import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Plus } from "lucide-react";

import { api, errMsg } from "../../lib/api.js";
import { logError } from "../../lib/logger.js";
import { useToast } from "../../context/ToastContext.js";
import { useMe } from "../../context/MeContext.js";
import { DateInput } from "../../components/ui/date-input.js";
import { Combobox } from "../../components/ui/combobox.js";
import { Button } from "../../components/ui/button.js";
import { Input } from "../../components/ui/input.js";
import { SearchInput } from "../../components/ui/search-input.js";
import { Pagination } from "../../components/ui/pagination.js";
import { Textarea } from "../../components/ui/textarea.js";
import { Badge } from "../../components/ui/badge.js";
import { DropdownSelect } from "../../components/DropdownButton.js";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../../components/ui/table.js";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "../../components/ui/dialog.js";
import { EmptyState, Field, RatingInput, SectionCard, fmtDate, toEmployeeOptions, usePaged } from "./shared.js";
import type { PipCheckin, PipRecord } from "./types.js";

function statusVariant(status: string) {
  if (status === "CLOSED_IMPROVED") return "success" as const;
  if (status === "CLOSED_EXIT") return "error" as const;
  if (status === "EXTENDED") return "warning" as const;
  return "info" as const;
}

export default function PipPanel({ canManage, isHR }: { canManage: boolean; isHR: boolean }) {
  const toast = useToast();
  const me = useMe();
  // The PIP being extended: choosing "Extended" asks for the new end date first.
  const [extending, setExtending] = useState<PipRecord | null>(null);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [rows, setRows] = useState<PipRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [openCheckins, setOpenCheckins] = useState<PipRecord | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [search, setSearch] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get<PipRecord[]>("/performance/pip");
      setRows(res.data);
    } catch (e) {
      logError("PipPanel.load", e);
      toast.show(errMsg(e) ?? "Could not load PIP records", "error");
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    void load();
  }, [load]);

  // Search by employee name, employee ID or status; paged on the client like
  // the Skills catalog (the PIP list is loaded whole).
  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((p) =>
      [p.employee.firstName, p.employee.lastName, `${p.employee.firstName} ${p.employee.lastName}`, p.employee.employeeCode, p.status.replace(/_/g, " ")]
        .some((v) => v?.toLowerCase().includes(q)),
    );
  }, [rows, search]);
  const pages = usePaged(visible, search.trim());

  async function setStatus(pip: PipRecord, status: string) {
    if (status === "EXTENDED") {
      setExtending(pip);
      return;
    }
    const id = pip.id;
    try {
      await api.patch(`/performance/pip/${id}`, { status });
      toast.show("PIP updated", "success");
      void load();
    } catch (e) {
      logError("PipPanel.setStatus", e);
      toast.show(errMsg(e) ?? "Could not update the PIP", "error");
    }
  }

  return (
    <SectionCard
      // The page header already says "Performance Improvement Plans"; the
      // search takes the card header's left side instead.
      leading={
        <>
          <SearchInput
            className="w-72"
            placeholder="Search by employee, ID or status"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {!loading && rows.length > 0 && (
            <span className="text-[12px] text-surface-500">
              {visible.length} PIP{visible.length === 1 ? "" : "s"}
              {search.trim() ? " match" : ""}
            </span>
          )}
        </>
      }
      actions={
        // Only HR opens a PIP; managers follow up their team's.
        isHR ? (
          <Button size="sm" onClick={() => setShowCreate(true)}>
            <Plus size={14} /> New PIP
          </Button>
        ) : undefined
      }
    >
      {loading ? (
        <p className="py-8 text-center text-[13px] text-surface-400">Loading…</p>
      ) : rows.length === 0 ? (
        <EmptyState
          message="No PIP records."
          action={
            isHR ? (
              <Button size="sm" onClick={() => setShowCreate(true)}>
                <Plus size={14} /> Start the first PIP
              </Button>
            ) : undefined
          }
        />
      ) : visible.length === 0 ? (
        <EmptyState message="No PIP records match your search." />
      ) : (
        <>
        <div className="overflow-x-auto rounded-md border border-surface-200">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="pl-3">Employee</TableHead>
                <TableHead>Start date</TableHead>
                <TableHead>End date</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Created from</TableHead>
                <TableHead>Decision date</TableHead>
                <TableHead>Check-ins</TableHead>
                <TableHead className="pr-3 text-center">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {pages.pageItems.map((p) => (
                <TableRow key={p.id}>
                  <TableCell className="pl-3">
                    <div className="font-medium">
                      {p.employee.firstName} {p.employee.lastName}
                    </div>
                    <div className="text-[12px] text-surface-500">{p.employee.employeeCode}</div>
                  </TableCell>
                  <TableCell className="whitespace-nowrap text-surface-500">{fmtDate(p.startDate)}</TableCell>
                  <TableCell className="whitespace-nowrap text-surface-500">
                    {fmtDate(p.endDate)}
                    {p.previousEndDate && (
                      <div className="text-[11px] text-surface-400">extended from {fmtDate(p.previousEndDate)}</div>
                    )}
                  </TableCell>
                  <TableCell>
                    {/* Nobody changes their own PIP; once HR has closed it, it stays closed
                        except for HR. Closing is HR's final decision. */}
                    {canManage && p.employeeId !== me.employeeId && (isHR || !p.decidedAt) ? (
                      <DropdownSelect
                        value={p.status}
                        onChange={(v) => void setStatus(p, v)}
                        options={[
                          { value: "ACTIVE", label: "Active" },
                          { value: "EXTENDED", label: "Extended" },
                          // Only HR closes a PIP, so the closing outcomes are
                          // left out of everyone else's list entirely.
                          ...(isHR ? [
                            { value: "CLOSED_IMPROVED", label: "Closed — improved" },
                            { value: "CLOSED_EXIT", label: "Closed — exit" },
                          ] : []),
                        ]}
                        placeholder="Status"
                        ariaLabel="PIP status"
                        clearable={false}
                        className="w-44 justify-between"
                        open={openDropdown === p.id}
                        onToggle={() => setOpenDropdown((d) => (d === p.id ? null : p.id))}
                        onClose={() => setOpenDropdown((d) => (d === p.id ? null : d))}
                      />
                    ) : (
                      <Badge tone={statusVariant(p.status)}>{p.status.replace(/_/g, " ")}</Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    {p.reviewId ? (
                      <Button asChild variant="ghost" size="sm">
                        <Link to={`/performance/reviews/${p.reviewId}`}>Annual review (probation)</Link>
                      </Button>
                    ) : (
                      <span className="text-[12px] text-surface-400">Started manually</span>
                    )}
                  </TableCell>
                  <TableCell className="whitespace-nowrap text-surface-500">
                    {p.decidedAt ? fmtDate(p.decidedAt) : <span className="text-surface-400">Pending</span>}
                  </TableCell>
                  <TableCell>
                    <Badge tone="outline">{p._count.checkins}</Badge>
                  </TableCell>
                  <TableCell className="pr-3 text-center">
                    <Button variant="outline" size="sm" onClick={() => setOpenCheckins(p)}>
                      Check-ins
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        <Pagination meta={pages.meta} onPageChange={pages.setPage} />
        </>
      )}

      <CheckinsDialog pip={openCheckins} onClose={() => setOpenCheckins(null)} onSaved={load} />
      <ExtendPipDialog pip={extending} onClose={() => setExtending(null)} onSaved={load} />
      <CreatePipDialog open={showCreate} onClose={() => setShowCreate(false)} onSaved={load} />
    </SectionCard>
  );
}

// Monday of the current week, so a weekly check-in always lands on a stable key.
function currentWeekStart(): string {
  const now = new Date();
  const day = now.getUTCDay();
  const diff = day === 0 ? -6 : 1 - day;
  const monday = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() + diff));
  return monday.toISOString().slice(0, 10);
}

function CheckinsDialog({
  pip,
  onClose,
  onSaved,
}: {
  pip: PipRecord | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const toast = useToast();
  const me = useMe();
  // The employee on the PIP writes their own comments; the manager or HR writes
  // the rating and manager comments. The server enforces the same split.
  const isEmployee = pip != null && pip.employeeId === me.employeeId;
  const [checkins, setCheckins] = useState<PipCheckin[]>([]);
  const [weekStartDate, setWeekStartDate] = useState(currentWeekStart());
  const [progressRating, setProgressRating] = useState<number | null>(null);
  const [managerComments, setManagerComments] = useState("");
  const [employeeComments, setEmployeeComments] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!pip) return;
    setWeekStartDate(currentWeekStart());
    setProgressRating(null);
    setManagerComments("");
    setEmployeeComments("");
    setError("");
    api
      .get<PipCheckin[]>(`/performance/pip/${pip.id}/checkins`)
      .then((r) => setCheckins(r.data))
      .catch((e) => logError("PipPanel.checkins", e));
  }, [pip]);

  async function save() {
    if (!pip) return;
    setSaving(true);
    setError("");
    try {
      await api.put(`/performance/pip/${pip.id}/checkins`, {
        weekStartDate,
        ...(isEmployee
          ? { employeeComments: employeeComments || null }
          : { progressRating, managerComments: managerComments || null }),
      });
      toast.show("Check-in saved", "success");
      const r = await api.get<PipCheckin[]>(`/performance/pip/${pip.id}/checkins`);
      setCheckins(r.data);
      onSaved();
    } catch (e) {
      logError("PipPanel.saveCheckin", e);
      setError(errMsg(e) ?? "Could not save the check-in");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open={pip != null} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            Weekly check-ins — {pip?.employee.firstName} {pip?.employee.lastName}
          </DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-4">
          {pip && <PipSummary pip={pip} />}

          <div className="rounded-md border border-surface-200 p-4">
            <p className="mb-3 text-[12px] font-semibold text-surface-500">Record a check-in</p>
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="Week starting">
                <DateInput isUS value={weekStartDate} onChange={setWeekStartDate} />
              </Field>
              {!isEmployee && (
                <Field label="Progress rating (1–5)">
                  <RatingInput value={progressRating} onChange={setProgressRating} />
                </Field>
              )}
            </div>
            {!isEmployee && (
              <div className="mt-3">
                <Field label="Manager comments">
                  <Textarea rows={2} value={managerComments} onChange={(e) => setManagerComments(e.target.value)} />
                </Field>
              </div>
            )}
            {isEmployee && (
              <div className="mt-3">
                <Field label="Your comments">
                  <Textarea rows={2} value={employeeComments} onChange={(e) => setEmployeeComments(e.target.value)} />
                </Field>
              </div>
            )}
            {error && <p className="mt-2 text-[13px] text-[var(--color-error-fg)]">{error}</p>}
            <div className="mt-3 flex justify-end">
              <Button size="sm" disabled={saving || !weekStartDate} onClick={() => void save()}>
                {saving ? "Saving…" : "Save check-in"}
              </Button>
            </div>
          </div>

          {checkins.length === 0 ? (
            <EmptyState message="No check-ins recorded yet." />
          ) : (
            <div className="overflow-x-auto rounded-md border border-surface-200">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="pl-3">Week</TableHead>
                    <TableHead>Rating</TableHead>
                    <TableHead>Manager</TableHead>
                    <TableHead className="pr-3">Employee</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {checkins.map((c) => (
                    <TableRow key={c.id}>
                      <TableCell className="pl-3 whitespace-nowrap">{fmtDate(c.weekStartDate)}</TableCell>
                      <TableCell className="tabular-nums">{c.progressRating ?? "—"}</TableCell>
                      <TableCell className="whitespace-pre-wrap">{c.managerComments || "—"}</TableCell>
                      <TableCell className="pr-3 whitespace-pre-wrap">{c.employeeComments || "—"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

interface EmployeeOption {
  id: string;
  firstName: string;
  lastName: string;
  employeeCode: string;
}

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

function plusDaysIso(days: number): string {
  return new Date(Date.now() + days * 86_400_000).toISOString().slice(0, 10);
}

// Manual PIP, for managers and HR. The server re-checks the role. A PIP opened
// from a probation outcome goes through the review instead and links back to it.
function CreatePipDialog({
  open,
  onClose,
  onSaved,
}: {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  const toast = useToast();
  const [employees, setEmployees] = useState<EmployeeOption[]>([]);
  const [form, setForm] = useState({
    employeeId: "",
    startDate: todayIso(),
    endDate: plusDaysIso(30),
    reason: "",
    goals: "",
    notes: "",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    setForm({ employeeId: "", startDate: todayIso(), endDate: plusDaysIso(30), reason: "", goals: "", notes: "" });
    setError("");
    api
      .get<{ data: EmployeeOption[] }>("/employees?limit=200")
      .then((r) => setEmployees(r.data.data))
      .catch((e) => logError("PipPanel.employees", e));
  }, [open]);

  async function save() {
    setSaving(true);
    setError("");
    try {
      await api.post("/performance/pip", {
        employeeId: form.employeeId,
        startDate: form.startDate,
        endDate: form.endDate,
        reason: form.reason,
        goals: form.goals,
        ...(form.notes ? { notes: form.notes } : {}),
      });
      toast.show("PIP started", "success");
      onSaved();
      onClose();
    } catch (e) {
      logError("PipPanel.create", e);
      setError(errMsg(e) ?? "Could not start the PIP");
    } finally {
      setSaving(false);
    }
  }

  const datesValid = !!form.startDate && !!form.endDate && form.endDate >= form.startDate;
  const ready = !!form.employeeId && datesValid && form.reason.trim() !== "" && form.goals.trim() !== "";

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>Start a performance improvement plan</DialogTitle>
        </DialogHeader>
        <div className="grid gap-4">
          <Field label="Employee">
            <Combobox
              value={form.employeeId}
              onValueChange={(v) => setForm((f) => ({ ...f, employeeId: v }))}
              options={toEmployeeOptions(employees)}
              placeholder="Select an employee"
              searchPlaceholder="Search by name or employee ID"
              emptyText="No employee matches."
            />
          </Field>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Start date">
              <DateInput isUS
                value={form.startDate}
                onChange={(v) => setForm((f) => ({ ...f, startDate: v }))}
              />
            </Field>
            <Field label="End date" hint={datesValid ? undefined : "End date cannot be before the start date."}>
              <DateInput isUS
                value={form.endDate}
                min={form.startDate || undefined}
                onChange={(v) => setForm((f) => ({ ...f, endDate: v }))}
              />
            </Field>
          </div>
          <Field label="Reason">
            <Textarea
              rows={3}
              value={form.reason}
              onChange={(e) => setForm((f) => ({ ...f, reason: e.target.value }))}
              placeholder="Why this PIP is being started"
            />
          </Field>
          <Field label="Goals" hint="What the employee needs to achieve by the end date. One per line works well.">
            <Textarea
              rows={4}
              value={form.goals}
              onChange={(e) => setForm((f) => ({ ...f, goals: e.target.value }))}
            />
          </Field>
          <Field label="Notes (optional)">
            <Textarea
              rows={2}
              value={form.notes}
              onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
            />
          </Field>
          {error && <p className="text-[13px] text-[var(--color-error-fg)]">{error}</p>}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button disabled={saving || !ready} onClick={() => void save()}>
            {saving ? "Starting…" : "Start PIP"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function addDaysIso(iso: string, days: number): string {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

// Extending a PIP pushes its end date out, so choosing "Extended" asks for the
// new end date (after the current one) before anything is saved.
function ExtendPipDialog({
  pip,
  onClose,
  onSaved,
}: {
  pip: PipRecord | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const toast = useToast();
  const [endDate, setEndDate] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!pip) return;
    setEndDate("");
    setError("");
  }, [pip]);

  const current = pip ? pip.endDate.slice(0, 10) : "";
  // The earliest allowed new end date is the day after the current one; the
  // calendar disables everything before it.
  const earliest = current ? addDaysIso(current, 1) : undefined;
  const valid = !!endDate && endDate > current;

  async function save() {
    if (!pip) return;
    setSaving(true);
    setError("");
    try {
      await api.patch(`/performance/pip/${pip.id}`, { status: "EXTENDED", endDate });
      toast.show("PIP extended", "success");
      onSaved();
      onClose();
    } catch (e) {
      logError("PipPanel.extend", e);
      setError(errMsg(e) ?? "Could not extend the PIP");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open={pip != null} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            Extend PIP — {pip?.employee.firstName} {pip?.employee.lastName}
          </DialogTitle>
        </DialogHeader>
        <div className="grid gap-4">
          <p className="text-[13px] text-surface-500">Current end date: {fmtDate(current)}</p>
          <Field label="New end date" hint={endDate && !valid ? "Must be after the current end date." : undefined}>
            <DateInput isUS value={endDate} onChange={setEndDate} min={earliest} />
          </Field>
          {error && <p className="text-[13px] text-[var(--color-error-fg)]">{error}</p>}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button disabled={saving || !valid} onClick={() => void save()}>
            {saving ? "Saving…" : "Extend PIP"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// The PIP at a glance, at the top of the check-ins dialog: its window, any
// extension (what it was extended from, and how often), and why it exists.
function PipSummary({ pip }: { pip: PipRecord }) {
  return (
    <div className="rounded-md bg-surface-50 px-4 py-3 text-[13px]">
      <dl className="grid gap-x-6 gap-y-2 sm:grid-cols-4">
        <SummaryItem label="Status">
          <Badge tone={statusVariant(pip.status)}>{pip.status.replace(/_/g, " ")}</Badge>
        </SummaryItem>
        <SummaryItem label="Start date">{fmtDate(pip.startDate)}</SummaryItem>
        <SummaryItem label="End date">
          {fmtDate(pip.endDate)}
          {pip.previousEndDate && (
            <div className="text-[11px] text-surface-500">
              extended from {fmtDate(pip.previousEndDate)}
              {pip.extensionCount > 1 ? ` · ${pip.extensionCount} extensions` : ""}
            </div>
          )}
        </SummaryItem>
        <SummaryItem label="Decision date">{pip.decidedAt ? fmtDate(pip.decidedAt) : "Pending"}</SummaryItem>
      </dl>
      <dl className="mt-3 grid gap-x-6 gap-y-2 border-t border-surface-200 pt-3 sm:grid-cols-2">
        <SummaryItem label="Reason">
          <span className="whitespace-pre-wrap">{pip.reason}</span>
        </SummaryItem>
        <SummaryItem label="Goals">
          <span className="whitespace-pre-wrap">{pip.goals}</span>
        </SummaryItem>
      </dl>
    </div>
  );
}

function SummaryItem({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <dt className="text-[11px] font-semibold tracking-wide text-surface-400 uppercase">{label}</dt>
      <dd className="mt-0.5">{children}</dd>
    </div>
  );
}
